import {Collapse} from 'antd';
import React from 'react';

// Logo wordmark local (ảnh chị cung cấp). DB/Settings vẫn lưu chữ "Rentzy" trong FooterNote;
// FE thay chữ đó bằng ảnh này khi render trang sản phẩm.
import rentzyLogo from '~images/rentzy-wordmark.png';
import {buildImageUrl} from '~utils/helper';

const {Panel} = Collapse;

const BRAND_NAME = 'Rentzy';

/**
 * Các khối nội dung hiển thị thêm của trang sản phẩm (Item-55-56-57).
 *
 * Nội dung riêng từng sản phẩm lấy từ product-detail, nội dung dùng chung lấy từ
 * /v1/system-config/product-display-content. Toàn bộ khối này chỉ hiện với sản phẩm mã mới
 * (product.isNewDisplayContent), nên sản phẩm cũ giữ nguyên bố cục trước đây.
 */

/** Tách plain text nhiều dòng; chấp nhận \\n literal nếu API trả chuỗi bị escape kép. */
const splitContentLines = content =>
  `${content || ''}`
    .replace(/\\n/g, '\n')
    .split(/\r?\n/)
    .map(line => line.replace(/^[\s•\-–—*]+/, '').trim())
    .filter(Boolean);

/**
 * Text Rentzy nhập trong ERP/Settings là plain text nhiều dòng.
 *
 * Nhiều dòng thì mỗi dòng là một gạch đầu dòng (đúng mockup: Rentzy đảm bảo, Hướng dẫn cách đo,
 * Bảng size của hãng, Cách bảo quản...). Một dòng thì in thẳng, không thêm dấu chấm lẻ loi.
 */
const TextBlock = ({content}) => {
  const lines = splitContentLines(content);

  if (lines.length <= 1) {
    return <div className='display-block-text'>{lines[0] || content}</div>;
  }

  return (
    <ul className='display-block-list'>
      {lines.map((line, index) => (
        // eslint-disable-next-line react/no-array-index-key
        <li key={`${index}-${line}`}>{line}</li>
      ))}
    </ul>
  );
};

/**
 * Dòng chân trang: chữ "Rentzy" hiển thị bằng logo wordmark thay vì chữ thường, phần còn lại
 * giữ nguyên text Rentzy nhập trong Settings. Nội dung không chứa chữ "Rentzy" thì in như thường.
 */
const FooterNote = ({content}) => {
  const text = `${content || ''}`;
  const index = text.toLowerCase().indexOf(BRAND_NAME.toLowerCase());
  if (index < 0) {
    return <div className='display-footer-note'>{text}</div>;
  }

  return (
    <div className='display-footer-note'>
      {text.slice(0, index)}
      <img className='display-footer-logo' src={rentzyLogo} alt={BRAND_NAME} />
      {text.slice(index + BRAND_NAME.length)}
    </div>
  );
};

const SizeMeasurementTable = ({sizeMeasurements}) => (
  <div className='size-measurement-list'>
    {sizeMeasurements.map(item => (
      <div className='size-measurement-row' key={`${item.type}-${item.attributeValueId}`}>
        <span className='size-measurement-label'>{item.size}:</span>
        {/* Size chưa được đo thì chỉ hiện tên size, đợi check số đo. */}
        <span className='size-measurement-value'>{item.measurement || ''}</span>
      </div>
    ))}
  </div>
);

const SizeGuide = ({product, displayContent}) => {
  const sizeMeasurements = product.sizeMeasurements || [];
  const hasBrandChart = !!(product.brandSizeChart || product.brandSizeChartImageUrl);
  const showMeasureGuide = !displayContent.isSizeMeasureGuideHidden && !!displayContent.sizeMeasureGuide;

  if (!sizeMeasurements.length && !hasBrandChart && !showMeasureGuide) return null;

  return (
    <Collapse className='display-collapse' expandIconPosition='right' defaultActiveKey={['size']}>
      <Panel header='Hướng dẫn chọn size' key='size'>
        {sizeMeasurements.length ? (
          <div className='display-sub-block'>
            <div className='display-sub-title'>Bảng size thực tế</div>
            <SizeMeasurementTable sizeMeasurements={sizeMeasurements} />
          </div>
        ) : null}

        {/* Hướng dẫn cách đo hiện sẵn, không có nút thu gọn riêng. */}
        {showMeasureGuide ? (
          <div className='display-sub-block'>
            <div className='display-sub-title'>Hướng dẫn cách đo</div>
            <TextBlock content={displayContent.sizeMeasureGuide} />
            {displayContent.sizeMeasureGuideImageUrl ? (
              <img
                className='display-block-image'
                src={buildImageUrl(displayContent.sizeMeasureGuideImageUrl, '_x600')}
                alt='Hướng dẫn cách đo'
              />
            ) : null}
          </div>
        ) : null}

        {hasBrandChart ? (
          <Collapse className='display-collapse display-collapse-inner' expandIconPosition='right'>
            <Panel header='Bảng size ước tính của hãng' key='brand-size'>
              {product.brandSizeChart ? <TextBlock content={product.brandSizeChart} /> : null}
              {product.brandSizeChartImageUrl ? (
                <img
                  className='display-block-image'
                  src={buildImageUrl(product.brandSizeChartImageUrl, '_x600')}
                  alt='Bảng size ước tính của hãng'
                />
              ) : null}
            </Panel>
          </Collapse>
        ) : null}
      </Panel>
    </Collapse>
  );
};

const ProductDisplayContent = ({product = {}, displayContent = {}}) => {
  if (!product.isNewDisplayContent) return null;

  // Mỗi panel chỉ hiện khi có nội dung; khối dùng chung còn phải không bị Rentzy ẩn trong Settings.
  const collapsiblePanels = [
    {
      key: 'care',
      header: 'Cách bảo quản do sản phẩm khó xử lý nếu phát sinh lỗi',
      content: product.careInstructions,
    },
    {
      key: 'washing',
      header: 'Cách giặt',
      content: product.washingInstructions,
    },
    {
      key: 'guarantee',
      header: 'Rentzy đảm bảo',
      content: displayContent.isRentzyGuaranteeHidden ? '' : displayContent.rentzyGuarantee,
    },
  ].filter(panel => !!panel.content);

  const showColorDisclaimer = !displayContent.isColorDisclaimerHidden && !!displayContent.colorDisclaimer;
  const showFooterNote = !displayContent.isFooterNoteHidden && !!displayContent.footerNote;
  const showHashtags = !displayContent.isSeoHashtagsHidden && !!displayContent.seoHashtags;

  const sizeMeasurements = product.sizeMeasurements || [];
  const hasBrandChart = !!(product.brandSizeChart || product.brandSizeChartImageUrl);
  const showMeasureGuide = !displayContent.isSizeMeasureGuideHidden && !!displayContent.sizeMeasureGuide;
  const hasSizeGuide = sizeMeasurements.length > 0 || hasBrandChart || showMeasureGuide;

  // Không có gì để vẽ thì thôi, tránh khối trống dưới Empty cũ.
  if (
    !product.includedAccessories &&
    !showColorDisclaimer &&
    !hasSizeGuide &&
    !collapsiblePanels.length &&
    !showFooterNote &&
    !showHashtags &&
    !product.imageLink
  ) {
    return null;
  }

  return (
    <div className='product-display-content'>
      {product.includedAccessories ? (
        <div className='display-block'>
          <span className='display-block-title'>Phụ kiện đi kèm: </span>
          <span>{product.includedAccessories}</span>
        </div>
      ) : null}

      {showColorDisclaimer ? <TextBlock content={displayContent.colorDisclaimer} /> : null}

      <SizeGuide product={product} displayContent={displayContent} />

      {collapsiblePanels.length ? (
        <Collapse className='display-collapse' expandIconPosition='right'>
          {collapsiblePanels.map(panel => (
            <Panel header={panel.header} key={panel.key}>
              <TextBlock content={panel.content} />
            </Panel>
          ))}
        </Collapse>
      ) : null}

      {/*
        general_description (Mô tả chung) cố tình KHÔNG hiển thị ở đây: theo mockup ERP đây là ô
        ghi chú nội bộ (mã sản phẩm dễ lem màu, lưu ý QC trước khi vô kho...) và bản mockup
        "Nội dung hiển thị MÔ TẢ trên Mobile" không có khối này. Dữ liệu vẫn được lưu và sửa
        trong ERP.
      */}

      {showFooterNote ? <FooterNote content={displayContent.footerNote} /> : null}
      {showHashtags ? <div className='display-hashtags'>{displayContent.seoHashtags}</div> : null}

      {product.imageLink ? (
        <div className='display-block display-block-link'>
          <span className='display-block-title'>Link hình ảnh: </span>
          <a href={product.imageLink} target='_blank' rel='noreferrer'>
            {product.imageLink}
          </a>
        </div>
      ) : null}
    </div>
  );
};

/**
 * Sản phẩm mã mới đã có khối mô tả (size / giặt / disclaimer...) thì không hiện Empty
 * "Sản phẩm chưa có mô tả chi tiết!" — API có thể không còn trả description cũ.
 */
export const hasProductDisplayContent = (product = {}, displayContent = {}) => {
  if (!product.isNewDisplayContent) return false;

  const sizeMeasurements = product.sizeMeasurements || [];
  const hasBrandChart = !!(product.brandSizeChart || product.brandSizeChartImageUrl);
  const showMeasureGuide = !displayContent.isSizeMeasureGuideHidden && !!displayContent.sizeMeasureGuide;

  return !!(
    product.includedAccessories ||
    product.careInstructions ||
    product.washingInstructions ||
    product.imageLink ||
    sizeMeasurements.length ||
    hasBrandChart ||
    showMeasureGuide ||
    (!displayContent.isColorDisclaimerHidden && displayContent.colorDisclaimer) ||
    (!displayContent.isRentzyGuaranteeHidden && displayContent.rentzyGuarantee) ||
    (!displayContent.isFooterNoteHidden && displayContent.footerNote) ||
    (!displayContent.isSeoHashtagsHidden && displayContent.seoHashtags)
  );
};

export default ProductDisplayContent;
